import { clsx } from '@alexis/helpers/clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Image from '@components/Image';

import { mod } from '@helpers/hotel';
import {
  cloudflareImageTransformations,
  cloudinaryImageTransformations,
  isCloudinarySrc,
} from '@helpers/imageTransformations';

import ChevronRight from '@icons/chevron-right-small.svg';
import ChevronLeft from '@icons/chevron_left.svg';

import { getIsRtlState } from '@redux/selectors';

import { HotelImage } from '@wegoTypes/hotels/hotelImage';

import styles from './HotelImageCarousel.module.scss';

interface HotelImageCarouselProps {
  className?: string;
  hotelImages: Array<HotelImage>;
  onSelectedIndexChanged(index: number): void;
  selectedIndex: number;
}

const NUMBER_OF_IMAGE_CONTAINER = 5;

const HotelImageCarousel: React.FC<HotelImageCarouselProps> = ({
  className,
  hotelImages,
  onSelectedIndexChanged,
  selectedIndex,
}): JSX.Element => {
  const isRtl = useSelector(getIsRtlState);
  const containerDivRef = useRef<HTMLDivElement>(null);
  const imageContainerDivRef = useRef<HTMLDivElement>(null);

  const [imageContainerWidth, setImageContainerWidth] = useState(0);
  const [startingLeft, setStartingLeft] = useState(0);
  const [hasClicked, setHasClicked] = useState(false);

  const noImagePlaceholderSrc = cloudinaryImageTransformations(
    'https://assets.wego.com/image/upload/v1639463186/web/misc/hotel-image-placeholder.png',
    'c_fill',
    'f_auto',
    'fl_lossy',
    'q_auto:low',
    'g_auto',
    'w_200',
  );
  const images = hotelImages.concat(
    Array(NUMBER_OF_IMAGE_CONTAINER - hotelImages.length).fill({ url: noImagePlaceholderSrc }),
  );

  const imageUrlIndexs = useMemo<Array<number>>(() => {
    return [
      selectedIndex - 2 < 0 ? images.length + (selectedIndex - 2) : selectedIndex - 2,
      selectedIndex - 1 < 0 ? images.length + (selectedIndex - 1) : selectedIndex - 1,
      selectedIndex,
      selectedIndex + 1 > images.length - 1
        ? images.length - (selectedIndex + 1)
        : selectedIndex + 1,
      selectedIndex + 2 > images.length - 1
        ? Math.abs(images.length - (selectedIndex + 2))
        : selectedIndex + 2,
    ];
  }, [selectedIndex, hotelImages]);

  useEffect(() => {
    const initializeStartingLeft = (): void => {
      if (containerDivRef.current && imageContainerDivRef.current) {
        const hotelImageCarouselWidth = containerDivRef.current.clientWidth;
        const imageContainerWidth = imageContainerDivRef.current.clientWidth;

        setImageContainerWidth(imageContainerWidth);
        setStartingLeft((hotelImageCarouselWidth - imageContainerWidth) / 2);
      }
    };

    window.addEventListener('resize', initializeStartingLeft);

    initializeStartingLeft();

    return () => {
      window.removeEventListener('resize', initializeStartingLeft);
    };
  }, [containerDivRef]);

  const getImageTransformationsUrl = useCallback<(url: string) => string>((url: string) => {
    if (isCloudinarySrc(url)) {
      return cloudinaryImageTransformations(
        url,
        'w_200',
        'c_fill',
        'f_auto',
        'fl_lossy',
        'q_auto:best',
        'g_auto',
      );
    } else {
      return cloudflareImageTransformations(url, { format: 'auto', width: 200, quality: 90 });
    }
  }, []);

  const imageContainerPosition = (
    imageContainerIndex: number,
    selectedImageIndex: number,
    imageContainerWidth: number,
    startingLeft: number,
  ): number => {
    const position = mod(imageContainerIndex - selectedImageIndex, NUMBER_OF_IMAGE_CONTAINER);

    if (position > 2) {
      // starting from 0 to 2 (0,1,2), 3 is the maximum number of image displayed on screen.
      return (position - NUMBER_OF_IMAGE_CONTAINER) * imageContainerWidth + startingLeft;
    }
    return position * imageContainerWidth + startingLeft;
  };

  const handleLeftChevronClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setHasClicked(true);
    const newSelectedIndex = selectedIndex - 1;
    onSelectedIndexChanged(newSelectedIndex < 0 ? images.length - 1 : newSelectedIndex);
  };

  const handleRightChevronClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setHasClicked(true);
    const newSelectedIndex = selectedIndex + 1;
    onSelectedIndexChanged(newSelectedIndex > images.length - 1 ? 0 : newSelectedIndex);
  };

  return (
    <div ref={containerDivRef} className={clsx(styles.container, className)}>
      {[...Array(NUMBER_OF_IMAGE_CONTAINER).keys()].map((index: number) => {
        const imageUrlIndex = imageUrlIndexs[mod(index + 2 - selectedIndex, 5)];

        const leftIndex =
          selectedIndex - 1 < 0 ? images.length + (selectedIndex - 1) : selectedIndex - 1;
        const rightIndex =
          selectedIndex + 1 > images.length - 1
            ? images.length - (selectedIndex + 1)
            : selectedIndex + 1;

        return (
          <div
            key={index}
            className={clsx(
              styles.imageContainer,
              (selectedIndex === imageUrlIndex ||
                leftIndex === imageUrlIndex ||
                rightIndex === imageUrlIndex) &&
                styles.top,
            )}
            style={{
              [isRtl ? 'right' : 'left']: `${imageContainerPosition(
                index,
                selectedIndex,
                imageContainerWidth,
                startingLeft,
              )}px`,
              transition: hasClicked ? `${isRtl ? 'right' : 'left'} 200ms ease-out` : 'none',
            }}
            ref={imageContainerDivRef}
          >
            <Image
              className={styles.imageItem}
              noImagePlaceholderSrc={noImagePlaceholderSrc}
              src={getImageTransformationsUrl(images[imageUrlIndex].url)}
            />
          </div>
        );
      })}

      <button
        type='button'
        className={styles.prevButton}
        onClick={(e) => handleLeftChevronClick(e)}
      >
        <ChevronLeft className={styles.chevron} />
      </button>

      <button
        type='button'
        className={styles.nextButton}
        onClick={(e) => handleRightChevronClick(e)}
      >
        <ChevronRight className={styles.chevron} />
      </button>
    </div>
  );
};

export default HotelImageCarousel;
